/* istanbul ignore file */
import React, { useState, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import { graph } from '@adc/constants'
import {
  utils,
  ListingPopupCard,
  withApplicationContext,
  MapPin,
} from '@adc/ui-adc'
import { getMarkerPopupPixelOffset } from '../../util/mapUtils'
import { isMobile } from '../../util/osDetector'
import { trackMapPin } from '../../services/analytics'
import styles from './index.scss'
import { getMapPinTrackingInfo, mapPinTrackingType } from './helpers'

const cx = classnames.bind(styles)

const { getDisplayIndicator } = utils
const ICON_Z_INDEX_DEFAULT = 1
const ICON_Z_INDEX_PRIORITY = 1000
const BASE_DIMENSION = 17
const BASE_HOVER_DIMENSION = BASE_DIMENSION * 2.8
const ICON_DIMENSIONS = [BASE_DIMENSION, BASE_DIMENSION]
const ICON_DIMENSIONS_HOVER = [BASE_HOVER_DIMENSION, BASE_HOVER_DIMENSION]
const ICON_ANCHOR = [BASE_DIMENSION / 2, BASE_DIMENSION / 2]
const ICON_ANCHOR_HOVER = [BASE_HOVER_DIMENSION / 2, BASE_HOVER_DIMENSION / 2]
const BRIDGE_BELOW_OFFSET = -43
const BRIDGE_ABOVE_OFFSET = -73
const LABEL_LEFT_OFFSET = 6
const LABEL_TOP_OFFSET = 10
const DEFAULT_COLOR = '#97a0a6' // $utk-color-gray-medium

const Marker = ({
  listing,
  location,
  onMarkerClick,
  index,
  map,
  bounds,
  updateSelectedId,
  triggeredMouseEventFlag,
  setTriggeredMouseEventFlag,
  applicationContext,
  featureFlags,
}) => {
  const zoom = map?.getZoom() || 0
  const [isHovered, setIsHovered] = useState(null)
  const isMobileOsRef = useRef(isMobile())
  const markerRef = useRef(null)

  const isCancelled =
    listing?.listing_status_group === graph.enums.ListingStatusGroup.CANCELED

  const onClick = useCallback(
    arg => {
      let analyticsData = {}

      /**
       * NOTE - we only get analyticsData currently if user clicks on popup.  Clicking on
       * pin dot does not send this data.
       * TODO: enhance to get that data sent as well if applicable
       */
      if (arg.stopPropagation) {
        arg.stopPropagation()
      } else {
        analyticsData = arg
      }
      isMobileOsRef.current = isMobile()
      if (isCancelled && !isMobileOsRef.current) {
        arg.preventDefault()
        return
      }
      onMarkerClick(listing.listing_id)
      trackMapPin({
        listing,
        isMobileView: isMobileOsRef.current,
        featureFlags: applicationContext.featureFlags,
        ...analyticsData,
      })
    },
    [listing, onMarkerClick, isCancelled, isMobileOsRef, applicationContext]
  )

  const onMouseOver = useCallback(() => {
    setIsHovered(true)
    if (!triggeredMouseEventFlag) {
      updateSelectedId(listing?.listing_id)
    } else {
      setTriggeredMouseEventFlag(false)
    }
  }, [
    setIsHovered,
    triggeredMouseEventFlag,
    setTriggeredMouseEventFlag,
    updateSelectedId,
    listing?.listing_id,
  ])

  const onMouseOut = useCallback(() => {
    setIsHovered(false)
    updateSelectedId(0)
  }, [setIsHovered, updateSelectedId])

  const assetInfo = getDisplayIndicator(listing)

  const marker = {
    scaledSize: new window.google.maps.Size(...ICON_DIMENSIONS),
    anchor: new window.google.maps.Point(...ICON_ANCHOR),
  }

  const markerHovered = {
    scaledSize: new window.google.maps.Size(...ICON_DIMENSIONS_HOVER),
    anchor: new window.google.maps.Point(...ICON_ANCHOR_HOVER),
  }

  const popupOptions = bounds
    ? getMarkerPopupPixelOffset(location, bounds, zoom)
    : null

  const mapPinTrackingInfo = getMapPinTrackingInfo(
    listing,
    applicationContext.featureFlags
  )

  let label = ''
  if (mapPinTrackingInfo[mapPinTrackingType.UPDATED]) {
    label = mapPinTrackingType.UPDATED
  } else if (mapPinTrackingInfo[mapPinTrackingType.NEW]) {
    label = mapPinTrackingType.NEW
  }

  const labelColor = mapPinTrackingInfo[mapPinTrackingType.SEEN]
    ? assetInfo?.colorDarkFaded
    : assetInfo?.colorDark || DEFAULT_COLOR

  // hovered get's priority and if not hovered, markers with labels get priority
  const zIndexForHovers = isHovered
    ? ICON_Z_INDEX_PRIORITY * 2
    : ICON_Z_INDEX_PRIORITY * (label ? 2 : 1) - index - 1 ||
      ICON_Z_INDEX_DEFAULT

  const markerLeft = 0 - markerHovered.scaledSize.width / 2
  const markerTop = 0 - markerHovered.scaledSize.height / 2
  const dotLeft = (markerHovered.scaledSize.width - marker.scaledSize.width) / 2
  const dotTop =
    (markerHovered.scaledSize.height - marker.scaledSize.height) / 2

  return (
    <>
      <div
        className={cx('halo', {
          active: isHovered,
          inactive: isHovered === false, // null is for initial state
        })}
        style={{
          width: markerHovered.scaledSize.width,
          height: markerHovered.scaledSize.height,
          left: markerLeft,
          top: markerTop,
          zIndex: zIndexForHovers,
        }}
      />
      <a
        href={!isMobileOsRef.current ? listing.listing_page_path : false} // hide 'href' altogether on mobile
        ref={markerRef}
        className={cx('marker-container', { 'not-allowed': isCancelled })}
        style={{
          left: `${markerLeft}px`,
          top: `${markerTop}px`,
          zIndex: zIndexForHovers,
        }}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseOut}
        onClick={onClick}
      >
        {!isMobileOsRef.current && (
          <div
            className={cx('bridge', {
              active: isHovered,
              inactive: isHovered === false, // null is for initial state
            })}
            style={{
              bottom: !popupOptions?.alignBottom
                ? `${BRIDGE_ABOVE_OFFSET}px`
                : `${BRIDGE_BELOW_OFFSET}px`,
            }}
          />
        )}
        <MapPin
          isHovered={isHovered}
          listingId={listing.listing_id}
          dotAssetType={listing?.listing_configuration?.asset_type}
          seen={mapPinTrackingInfo[mapPinTrackingType.SEEN]}
          style={{
            top: `${dotTop}px`,
            left: `${dotLeft}px`,
            width: marker.scaledSize.width,
            height: marker.scaledSize.height,
          }}
        />
        {isHovered && popupOptions && !isMobileOsRef.current && (
          <div
            className={cx('info-card-wrapper')}
            style={{
              left: `${popupOptions?.pixelOffset?.width}px`,
              top: `${popupOptions?.pixelOffset?.height}px`,
              zIndex: zIndexForHovers,
            }}
          >
            <ListingPopupCard
              className={cx('info-card', {
                active: isHovered,
                inactive: isHovered === false, // null is for initial state
              })}
              linkProps={{
                onClick,
              }}
              listing={listing}
              elmId={`marker_popup_${listing.listing_id}`}
              isCancelled={isCancelled}
              featureFlags={featureFlags}
            />
          </div>
        )}
        {label && (
          <div
            className={cx('label')}
            data-elm-id='marker-label'
            style={{
              left: `${dotLeft + LABEL_LEFT_OFFSET}px`,
              top: `${dotTop - LABEL_TOP_OFFSET}px`,
              backgroundColor: labelColor,
              zIndex: zIndexForHovers,
            }}
          >
            {label}
          </div>
        )}
      </a>
    </>
  )
}

Marker.propTypes = {
  index: PropTypes.number,
  listing: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  map: PropTypes.object,
  bounds: PropTypes.object,
  updateSelectedId: PropTypes.func.isRequired,
  triggeredMouseEventFlag: PropTypes.bool.isRequired,
  setTriggeredMouseEventFlag: PropTypes.func.isRequired,
  applicationContext: PropTypes.object.isRequired,
  featureFlags: PropTypes.object,
}

export default withApplicationContext(Marker)
